import React, { useRef, useState, useEffect, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import * as THREE from 'three';

const Performance3DContainer = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%);
  position: relative;
  overflow: hidden;
  padding: 5rem 2rem;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.2rem;
  color: #94a3b8;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const VisualizationGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 3rem;
  margin-bottom: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Canvas3DContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  min-height: 600px;
  position: relative;
`;

const ControlPanel = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: fit-content;
`;

const ControlGroup = styled.div`
  margin-bottom: 2rem;
`;

const ControlLabel = styled.h4`
  color: #3b82f6;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ControlButton = styled(motion.button)`
  width: 100%;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  background: ${props => props.active ? 
    'linear-gradient(135deg, #3b82f6, #1d4ed8)' : 
    'rgba(255, 255, 255, 0.05)'};
  color: white;
  border: 1px solid ${props => props.active ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  text-align: left;

  &:hover {
    background: ${props => props.active ? 
      'linear-gradient(135deg, #1d4ed8, #1e40af)' : 
      'rgba(255, 255, 255, 0.1)'};
    transform: translateX(5px);
  }
`;

const PerformanceMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const MetricCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 0.5rem;
`;

const MetricLabel = styled.div`
  color: #94a3b8;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LoadingFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #3b82f6;
  font-size: 1.2rem;
`;

// 3D Coupler Component
function CouplerModel({ visualizationType, animationSpeed, showStress }) {
  const meshRef = useRef();
  const groupRef = useRef();
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);

  useFrame((state, delta) => {
    if (meshRef.current) {
      // Rotate based on animation speed
      meshRef.current.rotation.y += delta * animationSpeed;
      
      // Floating animation
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime * 2) * 0.1;
      
      // Stress visualization effect
      if (showStress) {
        const stress = Math.sin(state.clock.elapsedTime * 4) * 0.5 + 0.5;
        meshRef.current.material.emissive.setRGB(stress * 0.3, 0, 0);
      } else {
        meshRef.current.material.emissive.setRGB(0, 0, 0);
      }
    }
  });

  const createCouplerGeometry = () => {
    switch (visualizationType) {
      case 'threaded':
        return new THREE.CylinderGeometry(0.8, 0.8, 3, 16);
      case 'swaged':
        return new THREE.CylinderGeometry(0.6, 1.0, 3, 16);
      case 'grout':
        return new THREE.CylinderGeometry(1.0, 1.0, 3, 16);
      default:
        return new THREE.CylinderGeometry(0.8, 0.8, 3, 16);
    }
  };

  const geometry = createCouplerGeometry();

  return (
    <group ref={groupRef}>
      {/* Main Coupler */}
      <mesh
        ref={meshRef}
        geometry={geometry}
        position={[0, 0, 0]}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        onClick={() => setClicked(!clicked)}
        scale={clicked ? 1.2 : hovered ? 1.1 : 1}
      >
        <meshStandardMaterial
          color={hovered ? '#3b82f6' : '#8b949e'}
          metalness={0.9}
          roughness={0.1}
        />
      </mesh>

      {/* Rebar segments */}
      <mesh position={[-2, 0, 0]}>
        <cylinderGeometry args={[0.15, 0.15, 6, 8]} />
        <meshStandardMaterial color="#6b7280" metalness={0.8} roughness={0.2} />
      </mesh>
      
      <mesh position={[2, 0, 0]}>
        <cylinderGeometry args={[0.15, 0.15, 6, 8]} />
        <meshStandardMaterial color="#6b7280" metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Thread visualization for threaded couplers */}
      {visualizationType === 'threaded' && (
        <>
          {[...Array(8)].map((_, i) => (
            <mesh key={i} position={[0, -1.2 + i * 0.3, 0]}>
              <torusGeometry args={[0.85, 0.05, 4, 12]} />
              <meshStandardMaterial color="#06b6d4" />
            </mesh>
          ))}
        </>
      )}

      {/* Stress visualization */}
      {showStress && (
        <mesh position={[0, 0, 0]} scale={[1.1, 1.1, 1.1]}>
          <cylinderGeometry args={[0.85, 0.85, 3.1, 16]} />
          <meshBasicMaterial color="#ef4444" opacity={0.3} transparent />
        </mesh>
      )}

      {/* Labels */}
      <Html position={[0, 2, 0]} center>
        <div style={{
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {visualizationType} Coupler
        </div>
      </Html>
    </group>
  );
}

// Load Visualization Component
function LoadVisualization({ loadType, intensity }) {
  const groupRef = useRef();
  
  useFrame((state, delta) => {
    if (groupRef.current && loadType !== 'static') {
      // Animate based on load type
      const time = state.clock.elapsedTime;
      
      switch (loadType) {
        case 'tension':
          groupRef.current.scale.x = 1 + Math.sin(time * 2) * 0.1 * intensity;
          break;
        case 'compression':
          groupRef.current.scale.y = 1 - Math.sin(time * 2) * 0.1 * intensity;
          break;
        case 'fatigue':
          groupRef.current.rotation.z = Math.sin(time * 4) * 0.2 * intensity;
          break;
        default:
          break;
      }
    }
  });

  return (
    <group ref={groupRef}>
      {/* Load arrows */}
      {loadType === 'tension' && (
        <>
          {/* Left tension arrow */}
          <mesh position={[-4, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <coneGeometry args={[0.3, 1.2, 8]} />
            <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.3} />
          </mesh>
          <mesh position={[-3.2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.1, 0.1, 1.5, 8]} />
            <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.3} />
          </mesh>
          
          {/* Right tension arrow */}
          <mesh position={[4, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
            <coneGeometry args={[0.3, 1.2, 8]} />
            <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.3} />
          </mesh>
          <mesh position={[3.2, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
            <cylinderGeometry args={[0.1, 0.1, 1.5, 8]} />
            <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.3} />
          </mesh>
        </>
      )}
      
      {loadType === 'compression' && (
        <>
          {/* Top compression arrow */}
          <mesh position={[0, 3, 0]} rotation={[Math.PI, 0, 0]}>
            <coneGeometry args={[0.3, 1.2, 8]} />
            <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.3} />
          </mesh>
          <mesh position={[0, 2.2, 0]}>
            <cylinderGeometry args={[0.1, 0.1, 1.5, 8]} />
            <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.3} />
          </mesh>
          
          {/* Bottom compression arrow */}
          <mesh position={[0, -3, 0]}>
            <coneGeometry args={[0.3, 1.2, 8]} />
            <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.3} />
          </mesh>
          <mesh position={[0, -2.2, 0]}>
            <cylinderGeometry args={[0.1, 0.1, 1.5, 8]} />
            <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.3} />
          </mesh>
        </>
      )}
      
      {/* Force visualization lines */}
      {[...Array(5)].map((_, i) => (
        <mesh key={i} position={[0, 0, -2 + i]} rotation={[0, Math.PI / 2, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 6, 8]} />
          <meshStandardMaterial 
            color="#06b6d4" 
            opacity={0.5 + intensity * 0.5} 
            transparent 
          />
        </mesh>
      ))}
    </group>
  );
}

function Performance3D() {
  const [visualizationType, setVisualizationType] = useState('threaded');
  const [animationSpeed, setAnimationSpeed] = useState(0.5);
  const [showStress, setShowStress] = useState(false);
  const [loadType, setLoadType] = useState('static');
  const [loadIntensity, setLoadIntensity] = useState(0.5);

  const performanceData = {
    threaded: { strength: 125, speed: 15, cost: '35-140' },
    grout: { strength: 110, speed: 12, cost: '60-140' }
  };

  const currentData = performanceData[visualizationType] || performanceData.threaded;

  return (
    <Performance3DContainer id="performance-3d" data-section="performance-3d">
      <ContentWrapper>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <SectionTitle>3D Performance Visualization</SectionTitle>
          <SectionSubtitle>
            Explore coupler technology through interactive 3D models and stress analysis
          </SectionSubtitle>
        </motion.div>

        <VisualizationGrid>
          <Canvas3DContainer
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Canvas camera={{ position: [5, 2, 5], fov: 50 }}>
              <Suspense fallback={<Html><LoadingFallback>Loading 3D Model...</LoadingFallback></Html>}>
                <ambientLight intensity={0.6} />
                <directionalLight position={[10, 10, 5]} intensity={1} />
                <pointLight position={[-10, -10, -5]} intensity={0.5} color="#3b82f6" />
                
                <CouplerModel
                  visualizationType={visualizationType}
                  animationSpeed={animationSpeed}
                  showStress={showStress}
                />
                
                <LoadVisualization
                  loadType={loadType}
                  intensity={loadIntensity}
                />
                
                <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
              </Suspense>
            </Canvas>
          </Canvas3DContainer>

          <ControlPanel
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <ControlGroup>
              <ControlLabel>Coupler Type</ControlLabel>
              {['threaded', 'grout'].map((type) => (
                <ControlButton
                  key={type}
                  active={visualizationType === type}
                  onClick={() => setVisualizationType(type)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)} Coupler
                </ControlButton>
              ))}
            </ControlGroup>

            <ControlGroup>
              <ControlLabel>Load Analysis</ControlLabel>
              {[
                { value: 'static', label: 'Static Load' },
                { value: 'tension', label: 'Tension Test' },
                { value: 'compression', label: 'Compression' },
                { value: 'fatigue', label: 'Fatigue Cycle' }
              ].map((load) => (
                <ControlButton
                  key={load.value}
                  active={loadType === load.value}
                  onClick={() => setLoadType(load.value)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {load.label}
                </ControlButton>
              ))}
            </ControlGroup>

            <ControlGroup>
              <ControlLabel>Visualization Options</ControlLabel>
              <ControlButton
                active={showStress}
                onClick={() => setShowStress(!showStress)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {showStress ? 'Hide' : 'Show'} Stress Analysis
              </ControlButton>
            </ControlGroup>

            <PerformanceMetrics>
              <MetricCard
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <MetricValue>{currentData.strength}%</MetricValue>
                <MetricLabel>Strength Rating</MetricLabel>
              </MetricCard>
              
              <MetricCard
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <MetricValue>{currentData.speed}</MetricValue>
                <MetricLabel>Connections/Hour</MetricLabel>
              </MetricCard>
              
              <MetricCard
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <MetricValue>₹{currentData.cost}</MetricValue>
                <MetricLabel>Cost per Unit</MetricLabel>
              </MetricCard>
            </PerformanceMetrics>
          </ControlPanel>
        </VisualizationGrid>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          style={{
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '20px',
            padding: '2rem',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <h3 style={{ color: '#3b82f6', marginBottom: '1rem' }}>Interactive Features</h3>
          <p style={{ color: '#94a3b8', lineHeight: 1.6 }}>
            Use the controls to explore different coupler types, analyze load scenarios, 
            and visualize stress patterns. Click and drag to rotate the 3D model, 
            scroll to zoom, and toggle various visualization options to understand 
            the superior performance characteristics of rebar couplers.
          </p>
        </motion.div>
      </ContentWrapper>
    </Performance3DContainer>
  );
}

export default Performance3D;